import type { ReactElement } from "react";
import type { RaidResult, RaidSummary } from "@rietz/shared";
import { uiActions } from "../actions.js";
import { ACCENT_HI, ACCENT_WASH, BAD, GOOD, LINE, RADIUS_SM, WARN } from "../theme.js";
import { formatCount, formatEuros } from "./format.js";
import { Divider, Eyebrow, Modal, Note, Num, Stat, Title } from "./kit.js";
import { questNames } from "./questView.js";

/**
 * The raid-end screen: what happened, and the way back. A READOUT, never a
 * source of truth — the `profile` after `raidEnd` already has every consequence
 * applied. It renders after the death overlay in the HUD's tree and covers it.
 */

const RESULT_TITLE: Record<RaidResult, string> = {
  extracted: "Escaped!",
  died: "Killed",
  mia: "Missing in action",
};

const RESULT_COLOR: Record<RaidResult, string> = {
  extracted: GOOD,
  died: BAD,
  mia: WARN,
};

export interface SummaryScreenProps {
  summary: RaidSummary;
}

export function SummaryScreen(props: SummaryScreenProps): ReactElement {
  const s = props.summary;

  return (
    <Modal width={480} label={RESULT_TITLE[s.result]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Eyebrow>Raid over</Eyebrow>
        <Title size="xl" style={{ color: RESULT_COLOR[s.result] }}>
          {RESULT_TITLE[s.result]}
        </Title>
        <Note style={{ display: "block", minHeight: 18 }}>{subtitle(s)}</Note>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Stat label="Poachers killed" value={String(s.scavKills)} />
        <Stat label="Mercenaries killed" value={String(s.pmcKills)} />
        {/* ONLY WHEN IT HAPPENED. A "The Hunter killed: 0" on every one of
            the hundred raids in which nobody met him would turn the one line
            a player will remember reading into part of the furniture. */}
        {s.bossKills > 0 ? (
          <Stat label="The Hunter killed" value={String(s.bossKills)} />
        ) : null}
        <Stat
          label="Experience"
          value={<Num tone="accent">{`+${formatCount(s.xpGained)} XP`}</Num>}
        />
      </div>

      {/* THE LEVEL, and only when it moved. The screen does not have to know
          the ladder to say this: the server sent both ends of it (see
          `RaidSummary.levelBefore`), so the one thing worth celebrating is a
          comparison rather than a lookup. */}
      {s.levelAfter > s.levelBefore ? (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: RADIUS_SM,
            border: `1px solid ${LINE}`,
            background: ACCENT_WASH,
            textAlign: "center",
          }}
        >
          <Title size="lg" style={{ color: ACCENT_HI }}>
            Level up → {s.levelAfter}
          </Title>
        </div>
      ) : null}

      {s.questsAdvanced.length === 0 ? null : (
        <>
          <Divider />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Eyebrow>Quests</Eyebrow>
            {questNames(s.questsAdvanced).map((name) => (
              <span key={name} style={{ fontSize: 12.5 }}>
                {name}
              </span>
            ))}
            {s.rewardEuros > 0 ? (
              <Num tone="accent" style={{ fontSize: 14 }}>
                + {formatEuros(s.rewardEuros)}
              </Num>
            ) : null}
          </div>
        </>
      )}

      <button
        type="button"
        className="metadeploy"
        style={{ width: "100%", marginTop: 2 }}
        onClick={() => {
          uiActions().closeSummary();
        }}
      >
        Back to menu
      </button>
    </Modal>
  );
}

/** The one line under the verdict: which exit, or who. */
function subtitle(s: RaidSummary): string {
  if (s.result === "extracted") {
    return s.extractName === undefined ? "" : `Extracted via ${s.extractName}`;
  }
  if (s.result === "died") {
    return s.killedBy === undefined ? "" : `shot by ${s.killedBy}`;
  }
  return "Time ran out — everything you carried was lost.";
}
